import {AfterViewInit, Component, Input, OnDestroy, OnInit, output, ViewChild} from '@angular/core';
import {DvirSection} from '../../model/dvir-section';
import {CardComponent} from '../../../../../../shared/components/card/card.component';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatTooltip} from '@angular/material/tooltip';
import {FormsModule, NgForm, ReactiveFormsModule} from '@angular/forms';
import {CardContentDirective} from '../../../../../../shared/components/card/card-content.directive';
import {DvirGroup} from '../../model/dvir-group';
import {DvirQuestion} from '../../model/dvir-question';
import {Subscription} from 'rxjs';
import {DvirInspectionQuestionDataType} from '../../model/QuestionDatatype';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatOption, MatSelect} from '@angular/material/select';
import {DvirQuestionValueProviderDto} from '../../model/dto/DvirQuestionValueProviderDto';
import {Dropdown, DropdownOption} from '../../../../../../shared/models/dvir';
import {DvirService} from '../../../../../../data/dvir/dvir.service';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList} from '@angular/cdk/drag-drop';
import {NgClass} from '@angular/common';

@Component({
  selector: 'app-inspection-form-sections-edit',
  imports: [
    CardComponent,
    MatButton,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatTooltip,
    ReactiveFormsModule,
    CardContentDirective,
    FormsModule,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
    MatSelect,
    MatOption,
    MatCheckbox,
    MatProgressSpinner,
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    NgClass,
  ],
  templateUrl: './inspection-form-sections-edit.component.html',
  styleUrls: ['./inspection-form-sections-edit.component.scss', '../../../../../settings/settings-common.scss'],
})
export class InspectionFormSectionsEditComponent implements OnInit, AfterViewInit, OnDestroy {
  validChange = output<boolean>();
  @ViewChild(NgForm)
  form: NgForm;

  providers: DropdownOption[] = [];
  configs: { [key: string]: Dropdown[] } = {};

  loadingProviders = false;

  subscriptions: Subscription[] = [];
  protected readonly DvirInspectionQuestionDataType = DvirInspectionQuestionDataType;

  constructor(public dvirService: DvirService) {}

  private _sections: DvirSection[] = [];

  get sections(): DvirSection[] {
    return this._sections;
  }

  @Input()
  set sections(value: DvirSection[]) {
    this._sections = value;
  }

  ngOnInit() {
    this.loadingProviders = true;
    this.dvirService.getDropdownProviders().then(providers => {
      Promise.all(providers.map(d => this.dvirService.getDropdownLists(d.id)))
        .then(results => {
          providers.forEach((d, index) => {
            this.configs[d.id] = results[index];
          });
          this.providers = providers;
        })
        .finally(() => {
          this.loadingProviders = false;
        });
    });
  }

  ngAfterViewInit() {
    this.subscriptions.push(
      this.form.statusChanges.subscribe(change => {
        this.validChange.emit(change === 'VALID');
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  addSection() {
    this._sections.push(new DvirSection([new DvirGroup([new DvirQuestion()])]));
  }

  addGroup(section: DvirSection = undefined) {
    if (!section) {
      if (this.sections.length === 0) {
        this.addSection();
        return;
      } else {
        section = this.sections[0];
      }
    }
    const group = new DvirGroup();
    addNewQuestionToGroup(group);
    section.groups.push(group);
  }

  addQuestion(group: DvirGroup = undefined) {
    if (!group) {
      if (this.sections.length === 0) {
        this.addSection();
      }
      group = this.sections[0].groups[0];
    }
    addNewQuestionToGroup(group);
  }

  removeQuestion(section: DvirSection, group: DvirGroup, question: DvirQuestion) {
    const index = group.questions.indexOf(question);
    group.questions.splice(index, 1);
    if (group.questions.length === 0) {
      this.removeGroup(section, group);
    }
  }

  removeGroup(section: DvirSection, group: DvirGroup) {
    const index = section.groups.indexOf(group);
    section.groups.splice(index, 1);
    if (section.groups.length === 0) {
      section.groups.push(new DvirGroup());
    }
  }

  removeSection(section: DvirSection) {
    if (this.sections.length === 1) {
      delete section.title;
    } else {
      const index = this.sections.indexOf(section);
      this.sections.splice(index, 1);
    }
  }

  changeQuestionType(question: DvirQuestion, type: DvirInspectionQuestionDataType) {
    if (type === DvirInspectionQuestionDataType.TEXT) {
      delete question.valueProvider;
    } else {
      delete question.notApplicable;
      if (!question.valueProvider) {
        question.valueProvider = new DvirQuestionValueProviderDto();
      }
    }
    question.type = type;
  }

  getQuestionTypeValues(question: DvirQuestion) {
    return this.configs[question.valueProvider.type]?.find(type => type.id === question.valueProvider.config)?.values || [];
  }

  questionProviderChanged(question: DvirQuestion) {
    delete question.valueProvider.config;
    delete question.valueProvider.defaultValue;
  }

  questionTypeChanged(question: DvirQuestion) {
    delete question.valueProvider.defaultValue;
  }

  questionInGroupReordered(event: CdkDragDrop<any, any, { group: DvirGroup; question: DvirQuestion }>) {
    const {
      currentIndex,
      previousIndex,
      item: {
        data: { group },
      },
    } = event;

    const min = Math.min(currentIndex, previousIndex);
    const max = Math.max(currentIndex, previousIndex);
    const moveForward = currentIndex > previousIndex;

    const questions = group.questions;

    group.questions = questions.map((question, index) => {
      if (index < min || index > max) {
        return question;
      }
      if (index === currentIndex) {
        return questions[previousIndex];
      }

      return questions[index + (moveForward ? 1 : -1)];
    });
  }

  moveSectionUp(section: DvirSection) {
    const index = this.sections.findIndex(s => s.uuid === section.uuid);
    const prevIndex = index - 1;
    const prevSection = this.sections[prevIndex];
    if (prevSection) {
      this.sections[index] = Object.assign(prevSection, { id: index });
      this.sections[prevIndex] = Object.assign(section, { id: prevIndex });
    }
  }

  moveSectionDown(section: DvirSection) {
    const index = this.sections.findIndex(s => s.uuid === section.uuid);
    const nextIndex = index + 1;
    const nextSection = this.sections[nextIndex];
    if (nextSection) {
      this.sections[index] = Object.assign(nextSection, { id: index });
      this.sections[nextIndex] = Object.assign(section, { id: nextIndex });
    }
  }

  moveGroupUp(section: DvirSection, group: DvirGroup) {
    const index = section.groups.findIndex(g => g.uuid === group.uuid);
    const prevIndex = index - 1;
    const prevGroup = section.groups[prevIndex];
    if (prevGroup) {
      section.groups[index] = Object.assign(prevGroup, { id: index });
      section.groups[prevIndex] = Object.assign(group, { id: prevIndex });
    }
  }

  moveGroupDown(section: DvirSection, group: DvirGroup) {
    const index = section.groups.findIndex(g => g.uuid === group.uuid);
    const nextIndex = index + 1;
    const nextGroup = section.groups[nextIndex];
    if (nextGroup) {
      section.groups[index] = Object.assign(nextGroup, { id: index });
      section.groups[nextIndex] = Object.assign(group, { id: nextIndex });
    }
  }
}

function addNewQuestionToGroup(group: DvirGroup) {
  group.questions.push(new DvirQuestion(undefined));
}
